import { isNull, isUndefined, snakeCase } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import download from 'downloadjs';

import { getDocumentById, openDocument } from '../../../../services/document';
import { calculateNettingEngineAnalysis, downloadNettingEngineResults, enquireNetting, fetchAllNettingDocuments, getNettingDocument } from '../../../../services/opinion';
import { getDocumentUrl } from '../../../../utils/getDocumentUrl';
import { fetchOpinionSubCounterpartyTypesSuccessful } from '../../../admin/sub-counterparty-types/store/actions';
import { SubCounterpartyType } from '../../../admin/sub-counterparty-types/store/types';
import { getOriginalDocumentId, getOriginalDocuments, getOriginalDocumentsPageNumber } from '../../../documents/my-documents/store';
import { fetchDocumentsStarted, openDocumentSuccessful, openSecondaryDocumentStarted, resetSecondaryDocument, setSecondaryDocument, toggleDocumentInView } from '../../../documents/my-documents/store/actions';
import { ArkDocument, DocumentNameType, DocumentType, SelectedDocument } from '../../../documents/store/types';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import {
    calculateNettingEngineFailed,
    calculateNettingEngineSuccessful,
    downloadNettingEngineResultsFailed,
    downloadNettingEngineResultsStarted,
    downloadNettingEngineResultsSuccessful,
    documentNettingDetailsAutoUpdateCompleted,
    enquireOpinionFailed,
    enquireOpinionSuccessful,
    enquireSingleNettingOpinion,
    fetchNettingEngineDocumentsFailed,
    fetchNettingEngineDocumentsStarted,
    fetchNettingEngineDocumentsSuccessful,
    openNettingEngineDocumentFailed,
    openNettingEngineDocumentStarted,
    openNettingEngineDocumentSuccessful,
    resetNettingEnginePage,
    setNettingEnginePage,
    calculateNettingEngineStarted,
} from './actions';
import { getCurrentNettingEngineData, getLocalLawIsValidJurisdiction, getNettingAssumptions, getNettingEngineDocumentColumnSort, getNettingEngineDocumentFilters, getNettingEngineDocumentPageSize, getNettingEngineDocuments, getNettingEngineDocumentsPageNumber, getNettingEngineResults, getNettingEngineTableView, getSavedNettingEngineData, getUnauthorisedOpinionJurisdictions } from './selectors';
import { JurisdictionalNettingInformation, NettingEngineAgreement, NettingEngineTableView, NettingPage, NettingRequestData, NettingResponseData, OpinionNettingEngineActionTypes, ISDANettingEngineData } from './types';
import { getUpdatedKeys } from './utils';
import { getDatasetInstance, getDatasetInstanceTimeline } from '../../../datasets/instances/store/selectors';
import { DatasetInstanceTimeline, Instance, InstanceHierarchyResponse } from '../../../datasets/instances/store/types';
import { fetchInstanceHierarchy } from '../../../../services/datasetInstance';
import { documentNettingAnalysisUpdatedInstance } from '../../../datasets/instances/store/actions';
import { fetchAllEntitiesStarted, fetchAllNettingEntitiesStarted } from '../../../admin/entity/store/actions';
import { fetchAllMyCompanyEntitiesStarted } from '../../../documents/analytics/store/actions';
import { getChartBase64String } from '../../../../utils/analytics';
import { MimeType } from '../../../constants/MimeType';

export function* redirectNettingEnginePage({ payload }: ReturnType<typeof setNettingEnginePage>) {
    let navigationPath = '/opinions/netting-engine';
    if (payload !== NettingPage.SELECT) {
        navigationPath = `/opinions/netting-engine/${payload === NettingPage.DOCUMENTS ? 'documents' : 'theory'}`;
    } else {
        yield put(resetNettingEnginePage());
    }
    yield put(push(navigationPath));
}

function* redirectNettingEnginePageWatcher() {
    yield takeLatest(OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_PAGE, redirectNettingEnginePage);
}

export function* attemptCalculateNettingEngine({ payload }: ReturnType<typeof calculateNettingEngineStarted>) {
    try {
        const { autoUpdateOnCalculate } = payload;
        const nettingEngineDocument: ISDANettingEngineData = yield select(getCurrentNettingEngineData);
        const savedNettingEngineDocument: ISDANettingEngineData | null = yield select(getSavedNettingEngineData);
        const originalDocumentId: number | null = yield select(getOriginalDocumentId);
        let updatedKeys: string[] | undefined = [];
        if (!isNull(savedNettingEngineDocument)) {
            updatedKeys = getUpdatedKeys(nettingEngineDocument, savedNettingEngineDocument);
        } else {
            updatedKeys = undefined;
        }
        const { counterparty: { jurisdiction, counterpartyClassification, subCounterpartyTypeId, entityId }, governingLaw, agreement, productsTraded, counterpartyOffices, sectionTenAApplies, paymentMethod, automaticEarlyTerminationApplicable } = nettingEngineDocument;
        const nettingData: NettingRequestData = {
            counterpartyId: entityId || '',
            counterpartyJurisdiction: jurisdiction || '',
            governingLawJurisdiction: governingLaw || '',
            agreementType: agreement!,
            productTypes: productsTraded || [],
            branches: counterpartyOffices,
            counterpartyType: counterpartyClassification || ''
        };
        if (!isNull(sectionTenAApplies) && !isUndefined(sectionTenAApplies)) {
            nettingData.sectionTenAApplies = sectionTenAApplies;
        }
        if (!isNull(subCounterpartyTypeId)) {
            nettingData.subCounterpartyTypeId = subCounterpartyTypeId;
        }
        if (paymentMethod) {
            nettingData.paymentMethod = paymentMethod;
        }
        if (automaticEarlyTerminationApplicable) {
            nettingData.automaticEarlyTerminationApplicable = automaticEarlyTerminationApplicable;
        }
        const response: NettingResponseData = yield call(calculateNettingEngineAnalysis, { nettingData, autoUpdateOnCalculate, originalDocumentId, updatedKeys });
        yield put(calculateNettingEngineSuccessful(response));
    } catch (e) {
        yield put(calculateNettingEngineFailed((e as Error).message));
        toast.error('Unable to calculate the results. Please try again.');
    }
}

function* calculateNettingEngineTheoryWatcher() {
    yield takeLeading(OpinionNettingEngineActionTypes.CALCULATE_NETTING_ENGINE_STARTED, attemptCalculateNettingEngine);
}

export function* attemptNettingEngineOpinionEnquireAll() {
    try {
        const jurisdictions: string[] = yield select(getUnauthorisedOpinionJurisdictions);
        const request = { jurisdictions };
        if (jurisdictions.length > 0) {
            yield call(enquireNetting, request);
        }
        yield put(enquireOpinionSuccessful(jurisdictions));
        toast('Enquiry sent');
    } catch (e) {
        toast.error('Unable to enquire about opinions. Please try again.');
        yield put(enquireOpinionFailed());
    }
}

function* nettingEngineOpinionEnquireAllWatcher() {
    yield takeLeading(OpinionNettingEngineActionTypes.ENQUIRE_ALL_NETTING_OPINIONS_STARTED, attemptNettingEngineOpinionEnquireAll);
}

export function* attemptNettingEngineOpinionEnquireSingle({ payload }: ReturnType<typeof enquireSingleNettingOpinion>) {
    try {
        const unauthorisedJurisdictions: string[] = yield select(getUnauthorisedOpinionJurisdictions);
        const localLawJurisdiction: string | null = yield select(getLocalLawIsValidJurisdiction);
        const jurisdictions = isNull(localLawJurisdiction) ? [payload] : [payload, localLawJurisdiction];
        if (unauthorisedJurisdictions.includes(payload)) {
            const request = { jurisdictions };
            yield call(enquireNetting, request);
        }
        yield put(enquireOpinionSuccessful(jurisdictions));
        toast('Enquiry sent');
    } catch (e) {
        toast.error('Unable to enquire about opinion. Please try again.');
        yield put(enquireOpinionFailed());
    }
}

function* nettingEngineOpinionEnquireSingleWatcher() {
    yield takeLeading(OpinionNettingEngineActionTypes.ENQUIRE_SINGLE_NETTING_OPINION_STARTED, attemptNettingEngineOpinionEnquireSingle);
}

export function* attemptFetchAllNettingDocuments({ payload }: ReturnType<typeof fetchNettingEngineDocumentsStarted>) {
    try {
        const { pageNumber, documentType } = payload;
        const filters: TableFilters = yield select(getNettingEngineDocumentFilters);
        const columnSort: ColumnSort | undefined = yield select(getNettingEngineDocumentColumnSort);
        const pageSize: number = yield select(getNettingEngineDocumentPageSize);
        const { documents, total }: { documents: ArkDocument[], total: number } = yield call(fetchAllNettingDocuments, { filters, pageNumber, columnSort, pageSize, documentType });
        yield put(fetchNettingEngineDocumentsSuccessful(documents, total, pageNumber));
    } catch (e) {
        toast.error('Unable to fetch the latest netting engine documents. Please try again.');
        yield put(fetchNettingEngineDocumentsFailed((e as Error).message));
    }
}

function* attemptFetchAllNettingDocumentsWatcher() {
    yield takeLatest(OpinionNettingEngineActionTypes.FETCH_NETTING_ENGINE_DOCUMENTS_STARTED, attemptFetchAllNettingDocuments);
}

export function* attemptDownloadNettingEngineResults({ payload }: ReturnType<typeof downloadNettingEngineResultsStarted>) {
    try {
        const { svgHeight, svgWidth } = payload;
        const currentNettingEngineData: ISDANettingEngineData | null = yield select(getCurrentNettingEngineData);
        const currentNettingEngineResponse: NettingResponseData | null = yield select(getNettingEngineResults);

        if (!isNull(currentNettingEngineData) && !isNull(currentNettingEngineResponse)) {
            let { counterparty: { jurisdiction, counterpartyClassification, subCounterpartyTypeId, entityId }, governingLaw, agreement, productsTraded, counterpartyOffices, sectionTenAApplies, paymentMethod } = currentNettingEngineData;
            const nettingRequestData: NettingRequestData = {
                counterpartyId: entityId || '',
                counterpartyJurisdiction: jurisdiction || '',
                governingLawJurisdiction: governingLaw || '',
                agreementType: agreement!,
                productTypes: productsTraded || [],
                branches: counterpartyOffices,
                counterpartyType: counterpartyClassification || ''
            };
            if (!isNull(sectionTenAApplies) && !isUndefined(sectionTenAApplies)) {
                nettingRequestData.sectionTenAApplies = sectionTenAApplies;
            }
            if (!isNull(subCounterpartyTypeId)) {
                nettingRequestData.subCounterpartyTypeId = subCounterpartyTypeId;
            }
            if (!isUndefined(paymentMethod) && agreement === NettingEngineAgreement.ISDA_1992_MASTER) {
                nettingRequestData.paymentMethod = paymentMethod || '';
            }

            const { jurisdictionalInformation, overallSummary, overallClientSummary, sectionTenImpactsDecision }: NettingResponseData = currentNettingEngineResponse;

            let jurisdictionalResultsWithSvg: JurisdictionalNettingInformation[] = [];

            for (const jurisdictionalResult of jurisdictionalInformation) {
                const svgId = `netting-engine-${snakeCase(jurisdictionalResult.jurisdiction)}-outline`;
                const templateSize = 900; // This translates to 3 inches in a word doc which is just under half of the word doc width
                const svgBase64: string = yield call(getChartBase64String, svgId, templateSize, templateSize, svgWidth, svgHeight, templateSize, templateSize);
                jurisdictionalResultsWithSvg.push({ ...jurisdictionalResult, svgBase64 });
            }

            const assumptions: string[] = yield select(getNettingAssumptions);
            const summary: Blob | null = yield call(downloadNettingEngineResults, { ...nettingRequestData, jurisdictionalInformation: jurisdictionalResultsWithSvg, overallSummary, overallClientSummary, sectionTenImpactsDecision, assumptions });

            if (!isNull(summary)) {
                const mimeType = MimeType.PDF;
                const filename = 'Netting Engine Summary';
                yield call(download, summary, filename, mimeType);
            }
            yield put(downloadNettingEngineResultsSuccessful());
        }
    } catch (e) {
        toast.error('Unable to download the results. Please try again.');
        yield put(downloadNettingEngineResultsFailed((e as Error).message));
    }
}

function* downloadNettingEngineResultsWatcher() {
    yield takeLeading(OpinionNettingEngineActionTypes.DOWNLOAD_NETTING_ENGINE_RESULTS_STARTED, attemptDownloadNettingEngineResults);
}

// Netting Engine Document Pagination Sagas

export function* nettingEnginePaginationNext() {
    const pageNumber: number = yield select(getNettingEngineDocumentsPageNumber);
    const nettingEngineView: NettingEngineTableView = yield select(getNettingEngineTableView);
    const documentFetchType = nettingEngineView === NettingEngineTableView.EXISTING ? DocumentType.ORIGINAL : DocumentType.PRE_EXECUTION_DRAFT;
    yield put(fetchNettingEngineDocumentsStarted(pageNumber + 1, documentFetchType));
}

function* nettingEnginePaginationNextWatcher() {
    yield takeLatest(OpinionNettingEngineActionTypes.NETTING_ENGINE_DOCUMENTS_PAGINATION_NEXT, nettingEnginePaginationNext);
}

export function* nettingEnginePaginationPrevious() {
    const pageNumber: number = yield select(getNettingEngineDocumentsPageNumber);
    const nettingEngineView: NettingEngineTableView = yield select(getNettingEngineTableView);
    const documentFetchType = nettingEngineView === NettingEngineTableView.EXISTING ? DocumentType.ORIGINAL : DocumentType.PRE_EXECUTION_DRAFT;
    yield put(fetchNettingEngineDocumentsStarted(pageNumber - 1, documentFetchType));
}

function* nettingEnginePaginationPreviousWatcher() {
    yield takeLatest(OpinionNettingEngineActionTypes.NETTING_ENGINE_DOCUMENTS_PAGINATION_PREVIOUS, nettingEnginePaginationPrevious);
}

export function* nettingEngineTableConfigUpdated() {
    const nettingEngineView: NettingEngineTableView = yield select(getNettingEngineTableView);
    const documentFetchType = nettingEngineView === NettingEngineTableView.EXISTING ? DocumentType.ORIGINAL : DocumentType.PRE_EXECUTION_DRAFT;
    yield put(fetchNettingEngineDocumentsStarted(1, documentFetchType));
}

function* nettingEngineTableConfigUpdatedWatcher() {
    yield debounce(200, [OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS, OpinionNettingEngineActionTypes.CLEAR_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS, OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENT_TABLE_COLUMN_SORT, OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENTS_PAGE_SIZE], nettingEngineTableConfigUpdated);
}

export function* attemptOpenNettingEngineDocument({ payload }: ReturnType<typeof openNettingEngineDocumentStarted>) {
    const { documentId, shouldRedirect, isOpeningFromDocumentAnalysis } = payload;
    if (!isOpeningFromDocumentAnalysis) {
        try {
            const { nettingDocument, location, mimeType, originalDocumentId, latestDocumentId, subCounterpartyOptions }: { nettingDocument: ISDANettingEngineData, location: string, mimeType: string, originalDocumentId: number, latestDocumentId: number, subCounterpartyOptions: SubCounterpartyType[] } = yield call(getNettingDocument, { documentId });
            const document: ArkDocument = yield call(getDocumentById, { documentId });
            const documentBlob: Blob = yield call(openDocument, { location });
            const documentUrl: string = yield call(getDocumentUrl, mimeType, documentBlob);
            yield put(openDocumentSuccessful(documentUrl, document));
            if (originalDocumentId && latestDocumentId && originalDocumentId !== latestDocumentId) {
                const secondaryDocument: ArkDocument = yield call(getDocumentById, { documentId: latestDocumentId });
                yield put(openSecondaryDocumentStarted(secondaryDocument));
                yield put(setSecondaryDocument(secondaryDocument));
                yield put(toggleDocumentInView(SelectedDocument.SECONDARY));
            } else {
                yield put(resetSecondaryDocument());
                yield put(toggleDocumentInView(SelectedDocument.ORIGINAL));
            }
            yield put(fetchOpinionSubCounterpartyTypesSuccessful(subCounterpartyOptions));
            const { counterpartyNameKnown, counterparty: { entityId, jurisdiction, counterpartyClassification, subCounterpartyTypeId }, agreement, governingLaw, sectionTenAApplies, counterpartyIsMultiBranch, counterpartyOffices, paymentMethod } = nettingDocument;
            const completedData = [counterpartyNameKnown, entityId, jurisdiction, counterpartyClassification, agreement, governingLaw, counterpartyIsMultiBranch].every(option => !isNull(option));
            let optionalMeta = [subCounterpartyTypeId, sectionTenAApplies];
            if (document.documentNameId === DocumentNameType.ISDA_1992) {
                optionalMeta.push(paymentMethod);
            }
            const completedOptionalData = optionalMeta.every(option => !isNull(option) && !isUndefined(option)) && (!isNull(counterpartyIsMultiBranch) && (!counterpartyIsMultiBranch || (counterpartyIsMultiBranch && counterpartyOffices.length > 0)));
            const allDataCompletedForInstantCalculation = completedData && completedOptionalData;
            yield put(openNettingEngineDocumentSuccessful(nettingDocument, allDataCompletedForInstantCalculation));
            if (allDataCompletedForInstantCalculation) {
                yield put(calculateNettingEngineStarted());
            }
            if (shouldRedirect) {
                yield put(push(`/opinions/netting-engine/documents/${documentId}`));
            }
        } catch (e) {
            yield put(openNettingEngineDocumentFailed((e as Error).message));
            yield put(push('/opinions/netting-engine'));
            toast.error('Unable to open netting engine document. Please try again.');
        }
    }
}

function* openNettingEngineDocumentWatcher() {
    yield takeLatest(OpinionNettingEngineActionTypes.OPEN_NETTING_ENGINE_DOCUMENT_STARTED, attemptOpenNettingEngineDocument);
}

export function* handleNettingDataAutoUpdateCompleted({ payload }: ReturnType<typeof documentNettingDetailsAutoUpdateCompleted>) {
    const { hasUpdatedDatasets, hasUpdatedEntities, parentInstanceIds, originalDocumentId } = payload;
    if (hasUpdatedDatasets) {
        const currentParentInstance: Instance | null = yield select(getDatasetInstance);
        if (!isNull(currentParentInstance) && !isUndefined(currentParentInstance.instance.datasetInstanceId) && parentInstanceIds.includes(currentParentInstance.instance.datasetInstanceId)) {
            const timeline: DatasetInstanceTimeline | null = yield select(getDatasetInstanceTimeline);
            if (!isNull(timeline)) {
                const timelineInstance = timeline.instances.find(({ datasetInstanceId }) => currentParentInstance.instance.datasetInstanceId === datasetInstanceId);
                if (timelineInstance) {
                    const { datasetInstances }: InstanceHierarchyResponse = yield call(fetchInstanceHierarchy, { instance: timelineInstance });
                    yield put(documentNettingAnalysisUpdatedInstance(datasetInstances));
                }
            }
        }
    }

    if (hasUpdatedEntities) {
        yield put(fetchAllNettingEntitiesStarted());
        yield put(fetchAllEntitiesStarted());
        yield put(fetchAllMyCompanyEntitiesStarted());
    }

    const nettingEngineDocuments: ArkDocument[] = yield select(getNettingEngineDocuments);
    if (nettingEngineDocuments.map(({ documentId }) => documentId).includes(originalDocumentId)) {
        const pageNumber: number = yield select(getNettingEngineDocumentsPageNumber);
        const nettingEngineView: NettingEngineTableView = yield select(getNettingEngineTableView);
        const documentFetchType = nettingEngineView === NettingEngineTableView.EXISTING ? DocumentType.ORIGINAL : DocumentType.PRE_EXECUTION_DRAFT;
        yield put(fetchNettingEngineDocumentsStarted(pageNumber, documentFetchType));
    }

    const originalDocuments: ArkDocument[] = yield select(getOriginalDocuments);
    if (originalDocuments.map(({ documentId }) => documentId).includes(originalDocumentId)) {
        const pageNumber: number = yield select(getOriginalDocumentsPageNumber);
        yield put(fetchDocumentsStarted(pageNumber));
    }
}

function* nettingDataAutoUpdateWatcher() {
    yield takeLeading(OpinionNettingEngineActionTypes.DOCUMENT_NETTING_DETAILS_AUTO_UPDATE_COMPLETED, handleNettingDataAutoUpdateCompleted);
}

export function* opinionNettingEngineSaga() {
    yield all([
        fork(redirectNettingEnginePageWatcher),
        fork(calculateNettingEngineTheoryWatcher),
        fork(nettingEngineOpinionEnquireAllWatcher),
        fork(nettingEngineOpinionEnquireSingleWatcher),
        fork(attemptFetchAllNettingDocumentsWatcher),
        fork(nettingEnginePaginationNextWatcher),
        fork(nettingEnginePaginationPreviousWatcher),
        fork(nettingEngineTableConfigUpdatedWatcher),
        fork(openNettingEngineDocumentWatcher),
        fork(downloadNettingEngineResultsWatcher),
        fork(nettingDataAutoUpdateWatcher)
    ]);
}
